import React, { useEffect, useRef } from "react";
import {
  loadTradingViewScript,
  createTradingViewWidget,
  generateContainerId,
  removeExistingScript,
  type TradingViewWidgetConfig,
} from "./util";
import { TRADINGVIEW_DEFAULT_SYMBOL, TRADINGVIEW_DEFAULT_INTERVAL } from "../../constants";
import "./styles.css";

interface TradingViewWidgetProps {
  symbol?: string;
  interval?: string;
  isCollapsed?: boolean;
  className?: string;
}

function TradingViewWidget({
  symbol = TRADINGVIEW_DEFAULT_SYMBOL,
  interval = TRADINGVIEW_DEFAULT_INTERVAL,
  isCollapsed = false,
  className = "",
}: TradingViewWidgetProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);
  const containerIdRef = useRef<string>(generateContainerId());

  useEffect(() => {
    // Don't initialize if collapsed
    if (isCollapsed || !containerRef.current) {
      return;
    }

    // Set container ID
    if (containerRef.current) {
      containerRef.current.id = containerIdRef.current;
    }

    let isMounted = true;

    // Load script and create widget
    loadTradingViewScript()
      .then(() => {
        if (!isMounted || !containerRef.current) {
          return;
        }

        const config: TradingViewWidgetConfig = {
          symbol,
          interval,
          containerId: containerIdRef.current,
        };

        widgetRef.current = createTradingViewWidget(config);
      })
      .catch((error) => {
        console.error("Failed to load TradingView widget:", error);
      });

    return () => {
      isMounted = false;
      if (widgetRef.current) {
        widgetRef.current = null;
      }
    };
  }, [symbol, interval, isCollapsed]);

  // Cleanup script on unmount
  useEffect(() => {
    return () => {
      removeExistingScript();
    };
  }, []);

  if (isCollapsed) {
    return <div className={`tradingview-widget-container ${className}`} />;
  }

  return (
    <div className={`tradingview-widget-wrapper ${className}`}>
      <div
        ref={containerRef}
        id={containerIdRef.current}
        className="tradingview-widget-container"
      />
    </div>
  );
}

export default TradingViewWidget;
